import * as React from 'react';
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Container, makeStyles } from '@material-ui/core';
import useSearchProfiles from '../../hooks/useSearchProfiles';
import JobSearchForm, { SearchFormAction } from '../../components/job-search/JobSearchForm';
import { jobSearch, JobPosting, JobSearchParams } from '../../services/job-search-service';
import { ignoreItem, LiveSearchAction, SearchProfile, SearchStatus } from '../../services/live-search';
import { JobSummaryAction } from '../../components/job-search/JobSummary';
import JobList from '../../components/job-search/JobList';
import SearchMap from '../../components/SearchMap';
import { DEFAULT_BOUNDS } from '../../components/MapContainer';
import insertJobAfter from './insertJobAfter';
import { useRef } from 'react';
import { MarkerProps } from '@react-google-maps/api';
import Loading from '../../components/Loading';
import { recordEvent } from '../../services/analytics';
import { SearchItemAction } from '../../actions/SearchItemAction';
import { AppContext } from '../../containers/App';

interface JobsPageState {
    loading?: boolean;
    jobs: JobPosting[];
    status: SearchStatus;
    showMap: boolean;
    params?: JobSearchParams;
    selectedJob?: string;
}

const initialState: JobsPageState = {
    jobs: [],
    status: {
        count: 0,
        totalCount: 0
    },
    showMap: false
};

/**
 * @param jobs
 * @param id
 * @return the index of the job with matching `id`, or `-1` if not found
 */
function getJobIndex(jobs: JobPosting[], id?: string | null) {
    if (id) {
        let i = jobs.length;
        while (i-- !== 0) {
            if (jobs[i].id === id) {
                return i;
            }
        }
    }

    return -1;
}

interface ItemWithLocation {
    id: string;
    title: string;
    location: {
        longitude: number;
        latitude: number;
    };
}
interface Marker extends MarkerProps {
    key: string;
    title: string;
    position: google.maps.LatLngLiteral;
}
interface ItemsWithLocationState {
    markers: Marker[];
    center: google.maps.LatLngLiteral;
    bounds: google.maps.LatLngBoundsLiteral;
}
function mapItemsToMarkersWithBounds(items: ItemWithLocation[], selectedJob?: string): ItemsWithLocationState {
    const markers: Marker[] = [];
    let center;
    let bounds: google.maps.LatLngBoundsLiteral = {
        north: -90,
        south: 90,
        east: -180,
        west: 180
    };
    for (const item of items) {
        if (!item.location) {
            continue;
        }
        markers.push({
            key: item.id,
            title: item.title,
            position: {
                lat: item.location.latitude,
                lng: item.location.longitude
            }
        });
        if (item.id === selectedJob) {
            center = {
                lat: item.location.latitude,
                lng: item.location.longitude
            };
        }
        if (item.location.latitude > bounds.north) {
            bounds.north = item.location.latitude;
        }
        if (item.location.latitude < bounds.south) {
            bounds.south = item.location.latitude;
        }
        if (item.location.longitude > bounds.east) {
            bounds.east = item.location.longitude;
        }
        if (item.location.longitude < bounds.west) {
            bounds.west = item.location.longitude;
        }
    }
    if (markers.length === 0) {
        bounds = DEFAULT_BOUNDS;
    }
    if (!center) {
        center = {
            lat: (bounds.north + bounds.south) / 2,
            lng: (bounds.east + bounds.west) / 2,
        };
    }
    return {markers, bounds, center};
}
function useItemsWithLocation(): [ItemsWithLocationState, (items: ItemWithLocation[], selectedJob?: string) => void] {
    const [state, setState] = useState<ItemsWithLocationState>({markers: [], bounds: DEFAULT_BOUNDS, center: {lat: 0, lng: 0}});
    return [state, (items: ItemWithLocation[], selectedJob?: string) => {
        setState(mapItemsToMarkersWithBounds(items, selectedJob));
    }];
}

function useJobsReducer(updateMarkers: (items: ItemWithLocation[], selectedJob?: string) => void, profile?: SearchProfile) {
    const reducer = (state: JobsPageState, action: LiveSearchAction<JobPosting> | JobSummaryAction | SearchFormAction | SearchItemAction) => {
        function updateJobs(jobs: JobPosting[]) {
            updateMarkers(jobs, state.selectedJob);
            return {...state, jobs};
        }

        switch (action.type) {
            case 'params':
                // autoSubmit = profile && profile.params && !state.status.submitted
                if ((action.payload.query || action.payload.location) && !profile) {
                    // console.info('have query or location but profile not yet loaded');
                    return {...state, loading: true};
                }
                // console.info('params:', action.payload);
                // state.status.submitted && state.status.count === 0
                return state;
            case 'search':
                // console.info('search params:', action.payload);
                return {...state, params: action.payload};
            case 'child_changed':
                let changed = getJobIndex(state.jobs, action.payload.id);
                // console.info('child_changed:', changed);
                if (changed >= 0) {
                    // Update the existing job
                    state.jobs[changed] = action.payload.data;
                    return {...state, jobs: state.jobs};
                }
            // tslint:disable-next-line:no-switch-case-fall-through - else add the job
            case 'child_added':
                let previous = getJobIndex(state.jobs, action.payload.previousSibling);
                return updateJobs(insertJobAfter(state.jobs, action.payload.data, previous));
            case 'child_removed':
                let removed = getJobIndex(state.jobs, action.payload.id);
                // console.info('child removed:', removed);
                if (removed >= 0) {
                    return updateJobs(state.jobs.slice(0, removed).concat(state.jobs.slice(removed + 1)));
                }
                return state;
            case 'child_moved':
                return state;
            case 'status':
                // console.info('status:', action.payload);
                if (action.payload.count !== 0) {
                    state.loading = false;
                }
                if (action.payload.count === 0 && state.jobs.length !== 0) {
                    state.jobs = [];
                }
                return {...state, status: {...state.status, ...action.payload}};
            case 'error':
                console.error(action.payload);
                return state;
            case 'ignore-item':
                console.info('ignore', action.payload);
                ignoreItem('jobs', action.payload.id, action.payload.method, action.payload.reason);
                recordEvent('JobSummary', 'IgnoreJob', action.payload.id);
                recordEvent('IgnoreItem', action.payload.method, action.payload.reason);
                return state;
            case 'focus-item':
                console.info('selected job:', action.payload.id);
                // updateMarkers(state.jobs, action.payload.id);
                return {...state, selectedJob: action.payload.id};
            case 'click-item-link':
                recordEvent('JobSummary', 'ClickJobLink', action.payload.listing.id);
                return state;
            case 'show-on-map':
                if (action.payload.listing) {
                    recordEvent('JobSummary', 'ShowOnMap', action.payload.listing.id);
                }
                if (action.payload.listing) {
                    updateMarkers(state.jobs, action.payload.listing.id);
                    return {...state, selectedJob: action.payload.listing.id, showMap: true};
                } else {
                    return {...state, showMap: false}; // selectedJob: undefined
                }
            default:
                throw new Error('Unexpected action: ' + JSON.stringify(action));
        }
    };

    return useReducer(reducer, initialState);
}

const useStyles = makeStyles({
    page: {
        '& .web-bot': {
            width: 600
        }
    }
}, {name: 'jobs'});

const Jobs = ({match, history}: RouteComponentProps<{profileName?: string}>) => {
    // console.info('Jobs.match:', props);
    // const [showMap, setShowMap] = useState(true);
    const classes = useStyles();
    const app = useContext(AppContext);
    const el = useRef<HTMLDivElement>(null);
    const selectedEl = useRef<HTMLDivElement | null>(null);
    const jobListBotHost = useRef<HTMLDivElement | null>(null);
    const [jobListContentHost] = useState<HTMLDivElement | null>(null);
    const [{markers, center, bounds}, updateMarkers] = useItemsWithLocation();
    const profile = useSearchProfiles('jobs', match && match.params.profileName)[1];
    const [state, dispatch] = useJobsReducer(updateMarkers, profile);
    const unsubscribe = useRef<Function | undefined>();
    // we can't search without a userId, and that is implied by profile.params
    const autoSubmit = profile && !state.status.submitted;
    // console.info('calculated autoSubmit. profile.params:', profile, 'status:', state.status);

    const location = JSON.parse(sessionStorage.getItem('location') as string);
    let userip = location ? location.ip : '127.0.0.1';

    // Ensure that we have `country` in the URL search params
    useEffect(() => {
        if (location) {
            const country = location.countryCode.toLowerCase();
            let search: string;
            if (!history.location.search) {
                search = '?country=' + country;
            } else if (!history.location.search.match(/[?&]country=/)) {
                search = history.location.search + '&country=' + country;
            }
            // @ts-ignore
            if (search) {
                history.replace(history.location.pathname + search, history.location.state);
            }
        }
    }, [location]);

    // When params are updated submit the search
    useEffect(() => {
        if (unsubscribe.current) {
            unsubscribe.current();
        }

        if (profile) {
            unsubscribe.current = jobSearch(profile.id, {
                ...state.params,
                userip,
                timestamp: Date.now()
            }, dispatch);
        }
    }, [state.params]);

    // Update page styling when showMap is toggled
    useEffect(() => {
        if (state.showMap) {
            document.body.classList.add('with-map');
        } else {
            document.body.classList.remove('with-map');
            if (selectedEl.current) {
                selectedEl.current.scrollIntoView();
            }
        }
    }, [state.showMap]);

    useEffect(() => {
        selectedEl.current = state.selectedJob ? el.current!.querySelector('.selected-item') : null;
    }, [state.selectedJob]);

    const jobSearchForm = useMemo(() => {
        // console.info('updating JobSearchForm. profile:', profile, 'autoSubmit:', autoSubmit);
        return (
            <JobSearchForm
                params={profile && profile.params || {}}
                autoSubmit={autoSubmit}
                dispatch={dispatch}
                history={history}
            />
        );
    }, [profile, autoSubmit]);

    const jobList = useMemo(() => {
        // console.info('updating jobList', state.jobs, state.selectedJob);
        const list = <JobList jobs={state.jobs} selectedJob={state.selectedJob} dispatch={dispatch}/>;
        // return jobListContentHost ? createPortal(list, jobListContentHost) : list;
        return list;
    }, [state.jobs, state.selectedJob, jobListContentHost]);
    // const jobListPortal = useMemo(() => {
    //     console.info('......................jobListContentHost:', jobListContentHost);
    //     if (true) {
    //     return null;
    //         } else {
    //         return jobListContentHost ? createPortal(
    //             jobList,
    //             // (jobListContentHost) || jobListBotHost.current || document.body
    //             jobListContentHost!
    //         ) : null;
    //     }
    // }, [jobList, jobListContentHost, jobListBotHost]);

    useEffect(() => {
        app.setBotLayout({
            hostDiv: jobListBotHost.current,
            children: jobList
        });
        // console.info('botRef:', botRef);
        // // setJobListContentHost(botRef && botRef.current);
        // if (false && botRef.current) {
        //     createPortal(<div>Foo</div>, botRef.current);
        // }

        return () => { app.setBotLayout({}); };
    }, [jobList, jobListBotHost]);

    // console.info('rendering Jobs page', profile, state.status, state.jobs ? state.jobs.length : 0); // JSON.stringify(state));
    return (
        <React.Fragment>
            {state.showMap && <SearchMap
                markers={markers}
                center={center}
                bounds={bounds}
                dispatch={dispatch}
                selectedEl={selectedEl}
            />}
            <Container
                className={classes.page}
                maxWidth="sm"
                ref={el}
            >
                {jobSearchForm}
                {/*(jobListPortal || jobList)}
                jobListContentHost ? null : jobList}
                */}
                <div className="jobListBotHost" ref={jobListBotHost}>
                    {state.loading ? <Loading/> : jobList}
                </div>
            </Container>
        </React.Fragment>
    );
};

export default Jobs;
